import React from 'react'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert from '@mui/material/Alert'
import Portal from '@mui/material/Portal'
import Slide from '@mui/material/Slide'
import type { SlideProps } from '@mui/material/Slide'
import { APIResult } from '../../utils/types'
import { bodyFont } from 'src/ui/theme/tokens'

type AlertSeverity = 'success' | 'warning' | 'error' | 'info'
export type AlertInput = {
  open: boolean
  severity?: AlertSeverity
  message?: string | undefined
  tone?: 'primary' | 'alt'
}

interface AlertProps {
  alert: AlertInput
  duration?: number
  onClose?: () => void
}

const alertStyles = {
  success: {
    background: '#eefaf3',
    color: '#176c57',
    border: '#b6decd',
    shadow: '#94cbb5',
    icon: '#d3f1e4',
  },
  warning: {
    background: '#fff4e8',
    color: '#944217',
    border: '#f0cfab',
    shadow: '#dfb28d',
    icon: '#ffe2c4',
  },
  error: {
    background: '#fff2f3',
    color: '#a73544',
    border: '#efc3ca',
    shadow: '#dfa2ad',
    icon: '#ffe1e6',
  },
  info: {
    background: '#eff9fd',
    color: '#193b58',
    border: '#bddfeb',
    shadow: '#9fc9dc',
    icon: '#d9f0fa',
  },
}

export default function Alert({
  alert,
  duration,
  onClose,
}: AlertProps): JSX.Element | null {
  const handleClose = () => {
    onClose?.()
  }
  const Transition = React.useMemo(
    () =>
      React.forwardRef(function AlertTransition(
        props: SlideProps & { children: React.ReactElement },
        ref: React.Ref<unknown>,
      ) {
        return <Slide direction="down" ref={ref} {...props} />
      }),
    [],
  )
  const severity = alert.severity || 'info'
  const tone = alert.tone
  const surface =
    alertStyles[
      tone === 'primary' ? 'info' : tone === 'alt' ? 'warning' : severity
    ]

  return alert.message ? (
    <Portal>
      <Snackbar
        open={alert.open}
        autoHideDuration={duration || 5000}
        onClose={handleClose}
        TransitionComponent={Transition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          position: 'fixed !important',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px) !important',
          left: '0 !important',
          right: '0 !important',
          transform: 'none !important',
          width: '100%',
          boxSizing: 'border-box',
          m: 0,
          px: { xs: 1.5, sm: 2 },
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: (theme) => theme.zIndex.snackbar + 1,
        }}
      >
        <MuiAlert
          onClose={handleClose}
          severity={severity}
          elevation={0}
          variant="standard"
          sx={{
            width: '100%',
            maxWidth: 560,
            minWidth: 0,
            boxSizing: 'border-box',
            minHeight: 60,
            p: { xs: 1.25, sm: 1.5 },
            border: `1px solid ${surface.border}`,
            borderRadius: '20px',
            fontFamily: bodyFont,
            fontWeight: 700,
            background: surface.background,
            color: surface.color,
            boxShadow: `0 3px 0 ${surface.shadow}, 0 10px 24px #193b5814`,
            backdropFilter: 'none',
            pointerEvents: 'auto',
            fontSize: { xs: 15, sm: 16 },
            lineHeight: 1.45,
            '& .MuiAlert-message': {
              p: 0,
              minWidth: 0,
              flex: 1,
              fontWeight: 700,
              textAlign: 'left',
              overflowWrap: 'anywhere',
            },
            '& .MuiAlert-icon': {
              m: 0,
              mr: 1.25,
              p: 0,
              width: 32,
              height: 32,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '10px',
              background: surface.icon,
              color: 'inherit',
              opacity: 1,
              '& .MuiSvgIcon-root': { fontSize: 21 },
            },
            '& .MuiAlert-action': {
              p: 0,
              ml: 1,
              alignSelf: 'center',
              flexShrink: 0,
              '& .MuiIconButton-root': {
                width: 28,
                height: 28,
                borderRadius: '10px',
                color: 'inherit',
                '&:hover': { background: surface.icon },
                '& .MuiSvgIcon-root': { fontSize: 18 },
              },
            },
          }}
        >
          {alert.message || ''}
        </MuiAlert>
      </Snackbar>
    </Portal>
  ) : null
}

export function handleAlert(
  alert: React.Dispatch<React.SetStateAction<AlertInput>>,
  res: APIResult,
  message: string,
): void {
  if (res?.success) {
    alert({
      open: true,
      message,
      severity: 'success',
    })
  } else {
    alert({
      open: true,
      message: res?.message || 'Error',
      severity: 'error',
    })
  }
}
